// submits/actions.js

import { Del } from "../../../models/Del/Del";
import { getDelInfo } from "../../../api/DelApi";
import { activeDelSubmitsInitialState } from "./activeDelsSubmitsState";
import { delFormInitialState } from "../../forms/delForm/delFormState";
// Importing the utility files for fetching del information
import { DelUtils } from "../utils/DelUtils";
// Importing the actions for the del form
import { makeDelFormActionsForTab } from "../../forms/delForm/delFormActions";

export const makeActiveDelSubmitsActions = (set, get) => ({
  // lifecycle
  startNewDel: () =>
    set((s) => ({
      activeDelSubmits: {
        ...s.activeDelSubmits,
        currentDel: new Del(),
        delMode: "new",
        delErrors: null,
        delActiveTab: "projects",
        delShowAll: false,
        delForm: {
          ...delFormInitialState,
          jsaFormUI: {
            ...delFormInitialState.jsaFormUI,
            base: {
              ...delFormInitialState.jsaFormUI.base,
              activeJsaTab: "All",
            },
          },
          wasteDisposalFormUI: {
            ...delFormInitialState.wasteDisposalFormUI,
            base: {
              ...delFormInitialState.wasteDisposalFormUI.base,
              activeWasteDisposalTab: "All",
            },
          },
        },
      },
    })),

  fetchDel: async (delMode, delId) => {
    set((s) => ({
      activeDelSubmits: { ...s.activeDelSubmits, isLoading: true, error: null },
    }));
    try {
      const info = await getDelInfo(delId, delMode);
      if (info.del_info === false) {
        set((s) => ({
          activeDelSubmits: {
            ...s.activeDelSubmits,
            isLoading: false,
            error: "DEL not found or could not be loaded.",
            currentDel: null,
            delMode: null,
          },
        }));
        return { ok: false, error: "DEL not found or could not be loaded." };
      }
      const newDel = new Del(info.del_info);
      // Transform the del data
      // To prevent errors we need to make sure that the projects and employeePositions lists are loaded and keep checking if they are not
      // Wait for data to load with timeout safety
      const maxWait = 10000; // 10 seconds
      const startTime = Date.now();
      while (get().projects.loading || get().employeePositions.loading) {
        if (Date.now() - startTime > maxWait) {
          console.warn(
            "Timeout waiting for projects/employeePositions to load"
          );
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      const projects = get().projects.list;
      const employeePositions = get().employeePositions.list;
      const delUtils = new DelUtils(
        newDel,
        delMode,
        projects,
        employeePositions
      );
      const transformedDel = delUtils.transformDelData();
      // Group available project-specific units by table name for the UI
      const rawPsu = info.del_info.available_project_specific_units || [];
      const groupedPsu = {};
      if (Array.isArray(rawPsu) && rawPsu.length > 0) {
        groupedPsu["all"] = rawPsu;
        rawPsu.forEach((unit) => {
          const tbl = unit.unit_table_name || "other";
          if (!groupedPsu[tbl]) groupedPsu[tbl] = [];
          groupedPsu[tbl].push(unit);
        });
      }
      set((s) => ({
        activeDelSubmits: {
          ...s.activeDelSubmits,
          isLoading: false,
          currentDel: transformedDel,
          delMode,
          delActiveTab: "projects",
          delShowAll: false,
          delForm: {
            ...delFormInitialState,
            projectUI: {
              ...delFormInitialState.projectUI,
              showJsaTab: !transformedDel.projectInfo.jsaNotRequired,
              showWasteDisposalTab:
                !transformedDel.projectInfo.wasteDisposalNotRequired,
              showClientSignaturesTab:
                !!transformedDel.projectInfo.clientSignaturesRequired,
            },
            unitsUI: {
              ...delFormInitialState.unitsUI,
              availableProjectUnits: groupedPsu,
            },
            jsaFormUI: {
              ...delFormInitialState.jsaFormUI,
              base: {
                ...delFormInitialState.jsaFormUI.base,
                activeJsaTab: "All",
              },
            },
            wasteDisposalFormUI: {
              ...delFormInitialState.wasteDisposalFormUI,
              base: {
                ...delFormInitialState.wasteDisposalFormUI.base,
                activeWasteDisposalTab: "All",
              },
            },
          },
        },
      }));
      return { ok: true };
    } catch (err) {
      set((s) => ({
        activeDelSubmits: {
          ...s.activeDelSubmits,
          isLoading: false,
          error: err,
          currentDel: null,
          delMode: null,
        },
      }));
      return { ok: false, error: err };
    }
  },

  clearDel: () =>
    set((s) => ({
      activeDelSubmits: {
        ...activeDelSubmitsInitialState,
        tabName: "submitted",
      },
    })),

  // UI helpers
  setActiveTab: (tabName) =>
    set((s) => ({
      activeDelSubmits: { ...s.activeDelSubmits, delActiveTab: tabName },
    })),
  setShowAll: (show) =>
    set((s) => ({
      activeDelSubmits: { ...s.activeDelSubmits, delShowAll: show },
    })),
  setDelErrors: (errors) =>
    set((s) => ({
      activeDelSubmits: { ...s.activeDelSubmits, delErrors: errors },
    })),

  delFormActions: Object.freeze(
    makeDelFormActionsForTab(set, get, "activeDelSubmits")
  ),
});
